import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { services, Data, imgUrl } from "../utils/environments/api";
import Loader from "../Components/Common/loader/Loaders.jsx";
import "../assets/css/servicedetailpage.css";

/* --- ReadMore component (improved styling + small icon) --- */
function ReadMore({ html, collapsedHeight = 140 }) {
  const [expanded, setExpanded] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(false);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // re-evaluate after DOM updates
    const taller = el.scrollHeight > collapsedHeight + 2;
    setNeedsToggle(taller);
    el.style.maxHeight = expanded ? `${el.scrollHeight}px` : `${collapsedHeight}px`;
  }, [html, collapsedHeight, expanded]);

  const toggle = () => {
    const el = contentRef.current;
    if (!el) return;
    if (!expanded) {
      el.style.maxHeight = `${el.scrollHeight}px`;
      setExpanded(true);
    } else {
      el.style.maxHeight = `${collapsedHeight}px`;
      setExpanded(false);
    }
  };

  return (
    <div className="readmore-wrapper modern">
      <div
        ref={contentRef}
        className={`service-desc-content ${expanded ? "expanded" : "collapsed"}`}
        dangerouslySetInnerHTML={{ __html: html || "" }}
      />
      {needsToggle && (
        <button className="readmore-toggle modern" onClick={toggle} aria-expanded={expanded}>
          <span>{expanded ? "Read less" : "Read more"}</span>
          <svg className={`chev ${expanded ? "open" : ""}`} width="14" height="14" viewBox="0 0 24 24">
            <path fill="currentColor" d="M7 10l5 5 5-5z" />
          </svg>
        </button>
      )}
    </div>
  );
}

/* --- Main Service component (keeps fetching logic) --- */
const Service = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");
  const [content, setContent] = useState({});
  const [serviceList, setServiceList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [serviceTypeRes, serviceContentRes] = await Promise.all([
          services.get("get_service_typ_api.php"),
          Data.get(`get_services_api.php?id=${id}`),
        ]);
        setServiceList(serviceTypeRes.data.data || []);
        setContent(serviceContentRes.data.data || {});
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return (
    <>
      {loading && <Loader />}
      <div className="service-page modern-page">
        {/* ====== PAGE TITLE ====== */}
           <section className="page-title">
          <div className="pattern-layer-one"></div>
          <div className="container">
            <h2>Services</h2>
            <ul className="page-breadcrumb">
              <li>
                <Link to="/">home &gt;</Link>
              </li>
              <li>Services</li>
            </ul>
          </div>
        </section>

        {/* ====== SERVICE DETAILS ====== */}
        <div className="service-detail-section modern-section">
          <div className="container">

            {/* ====== BLOCK 1 ====== */}
            <div className="service-block card">
              <div className="service-image image-left">
                <div className="img-inner">
                  <img src={`${imgUrl}${content.image1}`} alt={content.service} loading="lazy" />
                </div>
              </div>

              <div className="service-text">
                <div className="service-headline">
                  <span className="badge">Featured</span>
                  <h2 className="service-title">{content.title}</h2>
                </div>

                <ReadMore html={content.description1} collapsedHeight={150} />

                {/* <div className="service-meta">
                  <button className="cta">Get Quote</button>
                  <a className="learn-more" href="#contact">Talk to expert</a>
                </div> */}
              </div>
            </div>

            {/* ====== BLOCK 2 ====== */}
            <div className="service-block card reverse">
              <div className="service-image image-right">
                <div className="img-inner">
                  <img src={`${imgUrl}${content.image2}`} alt={content.service} loading="lazy" />
                </div>
              </div>

              <div className="service-text">
                <div className="service-headline">
                  <h3 className="service-subtitle">{content.sub_title}</h3>
                </div>

                <ReadMore html={content.description2} collapsedHeight={150} />

                {/* <div className="service-meta">
                  <button className="cta outline">Request Demo</button>
                  <Link className="learn-more" to="/services">All Services</Link>
                </div> */}
              </div>
            </div>

            {/* ====== OPTIONAL: key highlights (attractive cards) ====== */}
            <section className="key-highlights">
              <div className="highlight-grid">
                <div className="highlight-card">
                  <div className="icon">⚙️</div>
                  <h4>Seamless Integration</h4>
                  <p>Plug into existing systems with minimal friction and fast ROI.</p>
                </div>
                <div className="highlight-card">
                  <div className="icon">🔬</div>
                  <h4>R & D Expertise</h4>
                  <p>End-to-end product development from prototype to manufacturing.</p>
                </div>
                <div className="highlight-card">
                  <div className="icon">📊</div>
                  <h4>Data-Driven</h4>
                  <p>Real-time metrics & analytics to optimize performance.</p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
    </>
  );
};

export default Service;
